<template>
  <base-section
    v-if="hasRenderableContent"
    id="partnership"
    space="72"
  >
    <v-container style="max-width: 1160px">
      <base-section-heading
        v-if="moduleHeading || moduleSubheading"
        :title="moduleHeading"
        :subtitle="moduleSubheading"
        :is-uppercase="false"
        :space="6"
      />

      <v-row class="mb-6">
        <v-col cols="12">
          <base-body space="4">
            {{ moduleIntro }}
          </base-body>
          <div class="mt-4 d-flex flex-wrap">
            <v-btn
              color="primary"
              rounded
              depressed
              class="mr-3 mb-2"
              @click="goToContact"
            >
              {{ $t('partnership.primaryCta') }}
            </v-btn>
            <v-btn
              outlined
              rounded
              color="primary"
              class="mb-2"
              @click="goToContact"
            >
              {{ $t('partnership.secondaryCta') }}
            </v-btn>
          </div>
        </v-col>
      </v-row>

      <v-row>
        <v-col
          v-for="(item, index) in advantages"
          :key="index"
          cols="12"
          md="3"
        >
          <v-card
            class="pa-6 partnership-card d-flex flex-column"
            outlined
          >
            <div class="partnership-title">
              {{ item.title }}
            </div>
            <div class="partnership-desc flex-grow-1">
              {{ item.desc }}
            </div>
            <v-img
              v-if="item.imageUrl"
              :src="item.imageUrl"
              :alt="item.title || 'partnership'"
              class="partnership-image mt-auto"
            />
          </v-card>
        </v-col>
      </v-row>

      <v-row
        v-if="qaEnabled"
        class="mt-10"
        justify="center"
      >
        <v-col
          cols="12"
          md="8"
        >
          <base-section-heading
            v-if="qaTitle || qaSubtitle"
            :title="qaTitle || undefined"
            :subtitle="qaSubtitle || undefined"
            :size="qaTitleFontSize || undefined"
            :mobile-size="qaTitleMobileFontSize || 'text-h5'"
            :subtitle-size="qaSubtitleFontSize || undefined"
            :subtitle-mobile-size="qaSubtitleMobileFontSize || undefined"
            :is-uppercase="false"
            :space="4"
          />
          <v-expansion-panels accordion>
            <v-expansion-panel
              v-for="(qa, index) in qas"
              :key="index"
            >
              <v-expansion-panel-header>
                {{ qa.q }}
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                {{ qa.a }}
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </base-section>
</template>

<script>
  export default {
    name: 'SectionPartnership',

    data: () => ({
      moduleHeading: '',
      moduleSubheading: '',
      moduleIntro: '',
      moduleAdvantages: [],
      cmsQas: null,
      qaEnabled: false,
      qaTitle: '',
      qaSubtitle: '',
      qaTitleFontSize: '',
      qaTitleMobileFontSize: '',
      qaSubtitleFontSize: '',
      qaSubtitleMobileFontSize: '',
    }),

    computed: {
      hasRenderableContent() {
        return Boolean(
          String(this.moduleHeading || '').trim() ||
            String(this.moduleSubheading || '').trim() ||
            String(this.moduleIntro || '').trim() ||
            this.moduleAdvantages.length,
        )
      },
      advantages() {
        return this.moduleAdvantages
      },
      qas() {
        if (Array.isArray(this.cmsQas) && this.cmsQas.length > 0) {
          return this.cmsQas
        }
        return this.$i18n.getLocaleMessage(this.$i18n.locale).partnership?.qas || []
      },
    },

    watch: {
      '$i18n.locale'() {
        this.loadModuleContent()
      },
    },

    async mounted() {
      await this.loadModuleContent()
      window.addEventListener('resize', this.equalizeHeights)
    },

    beforeDestroy() {
      window.removeEventListener('resize', this.equalizeHeights)
    },

    methods: {
      equalizeHeights() {
        this.$nextTick(() => {
          const titles = this.$el.querySelectorAll('.partnership-title')
          const descs = this.$el.querySelectorAll('.partnership-desc')
          titles.forEach(el => { el.style.minHeight = '' })
          descs.forEach(el => { el.style.minHeight = '' })
          let maxT = 0
          titles.forEach(el => { maxT = Math.max(maxT, el.offsetHeight) })
          if (maxT > 0) titles.forEach(el => { el.style.minHeight = maxT + 'px' })
          let maxD = 0
          descs.forEach(el => { maxD = Math.max(maxD, el.offsetHeight) })
          if (maxD > 0) descs.forEach(el => { el.style.minHeight = maxD + 'px' })
        })
      },
      async loadModuleContent() {
        try {
          const modules = await this.$axios({
            method: 'get',
            url: '/public/modules/page/partnership',
            params: { lang: this.$i18n.locale },
          })

          // Load partnership_main module
          const module = (modules || []).find(item => item?.moduleKey === 'partnership_main')
          if (!module) {
            this.moduleHeading = ''
            this.moduleSubheading = ''
            this.moduleIntro = ''
            this.moduleAdvantages = []
          } else {
            this.moduleHeading = module.title || ''
            this.moduleSubheading = module.subtitle || ''
            this.moduleIntro = ''
            this.moduleAdvantages = []
            if (module.content) {
              try {
                const parsed = JSON.parse(module.content)
                if (parsed && typeof parsed === 'object') {
                  this.moduleIntro = parsed.intro || ''
                  this.moduleAdvantages = Array.isArray(parsed.advantages) ? parsed.advantages : []
                } else {
                  this.moduleIntro = module.content
                }
              } catch {
                this.moduleIntro = module.content
              }
            }
          }

          // Load qa module — only show when configured AND enabled
          const qaModule = (modules || []).find(m => m?.moduleKey === 'qa' && m?.enabled !== false)
          this.qaEnabled = !!qaModule
          this.qaTitle = qaModule?.title || ''
          this.qaSubtitle = qaModule?.subtitle || ''
          this.qaTitleFontSize = qaModule?.titleFontSize || ''
          this.qaTitleMobileFontSize = qaModule?.titleMobileFontSize || ''
          this.qaSubtitleFontSize = qaModule?.subtitleFontSize || ''
          this.qaSubtitleMobileFontSize = qaModule?.subtitleMobileFontSize || ''
          if (qaModule?.content) {
            try {
              const parsed = JSON.parse(qaModule.content)
              this.cmsQas = Array.isArray(parsed) ? parsed : null
            } catch {
              this.cmsQas = null
            }
          } else {
            this.cmsQas = null
          }
        } catch (error) {
          this.moduleHeading = ''
          this.moduleSubheading = ''
          this.moduleIntro = ''
          this.moduleAdvantages = []
          this.cmsQas = null
          this.qaEnabled = false
        }
        this.equalizeHeights()
      },
      goToContact() {
        this.$router.push({ name: 'Contact' })
      },
    },
  }
</script>

<style lang="sass" scoped>
.partnership-card
  height: 100%
  display: flex
  flex-direction: column
  border-radius: 0 !important

.partnership-title
  font-size: 18px
  font-weight: 600
  color: rgba(0, 0, 0, 0.9)

.partnership-desc
  margin-top: 10px
  color: rgba(0, 0, 0, 0.7)
  line-height: 1.6
  flex: 1

.partnership-image
  margin-top: 16px
  overflow: hidden
  border-radius: 0
</style>
